/**
 * Central authentication and user info management.
 * Fetches user information once and provides it to all scripts that need it.
 */
(() => {
  // Guard against double execution - the homepage loads this script both early (in <head>,
  // so getUserInfo() is ready for hero-banner) and again via the standard extra_javascript
  // inclusion, which would otherwise register every event listener below twice.
  if (window.__userAuthInitialized) {
    return;
  }
  window.__userAuthInitialized = true;

  // Singleton promise to ensure /_auth/me is only called once
  let userInfoPromise = null;

  /**
   * Fetches user information from /_auth/me endpoint.
   * Only fetches once - subsequent calls return the same promise.
   *
   * @returns {Promise<Object|null>} User info object or null if not authenticated
   */
  window.getUserInfo = function () {
    if (userInfoPromise) {
      return userInfoPromise;
    }

    userInfoPromise = (async () => {
      try {
        const response = await fetch("/_auth/me");
        if (response.ok) {
          return await response.json();
        }
        return null;
      } catch (error) {
        console.warn("Failed to fetch user info:", error);
        return null;
      }
    })();

    return userInfoPromise;
  };

  // Handle login link click - redirect to authentication endpoint
  const userLoginDiv = document.getElementById("userLogin");
  userLoginDiv.addEventListener("click", () => {
    // Redirect to login with return URL parameter
    document.location.href = `/_auth/login?ret=${encodeURIComponent(document.location.href)}`;
  });

  userLoginDiv.addEventListener("keyup", function (event) {
    if (event.keyCode === 13) {
      userLoginDiv.click();
    }
  });

  function getPicture(userInfo) {
    try {
      if (userInfo.picture) {
        return { avatar: userInfo.picture, email: userInfo.email };
      }
      const email = `${userInfo.email}`.trim().toLowerCase();
      const md5hash = md5(email);
      let initials = userInfo.given_name.substr(0, 1) + userInfo.family_name.substr(0, 1);
      if (initials.length !== 2) {
        initials = userInfo.email.substr(0, 2);
      }
      const backup = `https://cdn.auth0.com/avatars/${initials.toLowerCase()}.png?ssl=1`;
      const avatar = `https://s.gravatar.com/avatar/${md5hash}?d=${encodeURIComponent(backup)}`;
      return { avatar: avatar, email: email };
    } catch {
      return { avatar: undefined, email: undefined };
    }
  }

  function toggleUserMenu() {
    const menu = document.getElementById("userMenu");
    const loginImage = document.getElementById("login-image");
    toggleBoolAttribute(loginImage, "aria-expanded");
    if (menu.style.display === "none") {
      menu.style.display = "block";
    } else {
      menu.style.display = "none";
    }
  }
  const loginImage = document.getElementById("login-image");
  loginImage.addEventListener("click", toggleUserMenu);
  loginImage.addEventListener("keydown", function (event) {
    if (event.code === "Enter" || event.code === "Space") {
      event.preventDefault();
      loginImage.click();
    }
  });

  function closeMenuOnOutsideClick(event) {
    const menu = document.getElementById("userMenu");
    const image = document.getElementById("login-image");
    const target = event.target || event.srcElement;

    if (menu !== target && !menu.contains(target) && image !== target && !image.contains(target)) {
      menu.style.display = "none";
      image.setAttribute("aria-expanded", "false");
    }
  }

  document.addEventListener("click", closeMenuOnOutsideClick);

  const userLogoutLink = document.getElementById("userLogout");
  if (userLogoutLink) {
    userLogoutLink.addEventListener("click", () => {
      // Clear chatbot user info before logging out
      updateChatbotUserInfo(null);
      window.location.href = "/_auth/logout";
    });
    userLogoutLink.addEventListener("keyup", function (event) {
      if (event.keyCode === 13) {
        userLogoutLink.click();
      }
    });
  }

  async function toggleControls() {
    const userAvatar = document.getElementById("userAvatar");

    // Use centralized user info fetching
    const userInfo = await window.getUserInfo();

    if (!userInfo) {
      userLoginDiv.style.display = "block";
      userAvatar.style.display = "none";
      updateChatbotUserInfo(null);
    } else {
      userLoginDiv.style.display = "none";
      userAvatar.style.display = "block";
      showLoginImage(userInfo);
      updateChatbotUserInfo(userInfo);
    }
  }

  function updateChatbotUserInfo(userInfo) {
    const chatContainer = document.querySelector("chat-container");
    if (!chatContainer) {
      return; // Chatbot not present on this page
    }

    if (!userInfo) {
      // User is logged out - clear user info
      if (typeof chatContainer.setUserInfo === "function") {
        chatContainer.setUserInfo({ email: "", name: "" });
      }
      return;
    }

    // User is logged in - construct full name and set user info
    let fullName = userInfo.name || `${userInfo.given_name || ""} ${userInfo.family_name || ""}`.trim();

    // Fallback to email username if no name is available
    if (!fullName && userInfo.email) {
      fullName = userInfo.email.split("@")[0];
    }

    if (userInfo.email && typeof chatContainer.setUserInfo === "function") {
      chatContainer.setUserInfo({
        email: userInfo.email,
        name: fullName,
      });
    }
  }

  async function loadImageAsync(url) {
    return new Promise(async (resolve, reject) => {
      const image = new Image();

      image.onload = () => {
        resolve(image);
      };

      image.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      image.src = url;
    });
  }

  async function showLoginImage(userInfo) {
    const userInformation = getPicture(userInfo);
    const baseUrl = window.SITE_BASE_URL || "";
    const defaultAvatar = `${baseUrl}/assets/images/x-login.png`;
    if (userInformation.avatar && userInformation.email) {
      try {
        // show default image
        loginImage.src = defaultAvatar;
        const image = await loadImageAsync(userInformation.avatar);
        loginImage.src = image.src;
      } catch {
        loginImage.src = defaultAvatar;
      }
      loginImage.title = userInformation.email;
    }
  }

  // toggle controls (login vs user menu + gravatar) - show login control if the user is not logged in (no TOKEN cookie)
  toggleControls();
})();

function toggleBoolAttribute(element, attribute) {
  if (element.getAttribute(attribute) == "false") {
    element.setAttribute(attribute, "true");
  } else {
    element.setAttribute(attribute, "false");
  }
}
